/**
 * Activity.js — everything that happened on a deal, one line each
 * ================================================================
 * The bank rows, the broker trades, the tax lots, the notices, and the ledger
 * transactions each of them wrote — five registers kept by five parties
 * (CLAUDE.md 0.18), shown side by side rather than merged, so that "the bank
 * says" and "the book says" stay distinct.
 *
 * Usability review of 23 October 2026, item 24: *"Collapse each trade into one
 * line ("Sold 355,579 @ 0.1269, +SEK 44,785") and show the related records
 * only when the user expands it. Hide superseded and zero-value rows by
 * default. Add filters for date range and type. Add a monthly subtotal row."*
 *
 * So each row is one line, and what used to be the rows around it is under it:
 *
 *   - the ledger transactions a bank row, trade or notice wrote, and the lot a
 *     trade or notice made or drew on, open with the row. A lot a trade made is
 *     therefore not a second row beside the trade; a lot nothing claims — a
 *     paydown, an equity-line settlement booked from its document — still has
 *     a row of its own, and so does a hand journal nothing explains, because
 *     that is the one worth a second look.
 *   - superseded rows (a reversed transaction, the reversal that undid it, a
 *     re-dating, a lot the unposter retired) and rows that moved nothing are
 *     hidden until asked for, and the toggle says how many. Reachable rather
 *     than deleted: a correction nobody can see is the same problem with the
 *     alarm disconnected (0.27).
 *
 * Data: GET /api/v1/reports/deal-activity/?deal=<id>
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import { Badge, Card, EmptyState, LoadingSpinner } from "../../components/ui";
import { ExportCsvButton, InfoTip } from "../../components/PageTools";
import { money, toNumber } from "../../components/format";
import { ledgerPath } from "../../components/links";
import theme from "../../components/theme";
import { day, plainNumber, shares, signedMoney } from "./figures";
import { isRetiredNote, readerText } from "./text";

export const KIND = {
  bank: { label: "Bank", variant: "blue" },
  trade: { label: "Trade", variant: "amber" },
  lot: { label: "Lot", variant: "gray" },
  notice: { label: "Notice", variant: "yellow" },
  ledger: { label: "Ledger", variant: "green" },
};

/* The ledger writes `Debit` / `Credit` (core.models.LedgerSide), and this
   compared against "DEBIT", so every debit leg on every deal timeline printed
   as CR. Case-folded so either spelling reads right. */
export function sideLabel(side) {
  return String(side || "").toLowerCase() === "debit" ? "DR" : "CR";
}

/* A journal whose only "cash" leg is a claim between two Helena entities moved
   no money. It used to be labelled Cash in / Cash out with a signed amount, so
   the fund's recharge of Cubed Biotech's legal to Helena Partners Inc printed
   as +9,950.00 of cash in right under the 9,950.00 paid to the law firm.

   The words say only what the legs establish: which side of this entity's
   account with the other one moved, and — when every other leg is a lender's
   note on one side — which side the notes moved. */
function sideVerb(side) {
  return sideLabel(side) === "DR" ? "Charged to" : "Credited to";
}

export function intercompanyLabel(ic) {
  if (!ic) return "Between Helena's own books · no cash moved";
  const who = ic.counterparty;
  const account = ic.counterparty_side
    ? `${sideVerb(ic.counterparty_side)} ${who}'s account`
    : `Between us and ${who}`;
  if (ic.notes_side) {
    const rest = account.charAt(0).toLowerCase() + account.slice(1);
    return `${sideVerb(ic.notes_side)} the lenders' notes, ${rest} · no cash moved`;
  }
  return `${account} · no cash moved`;
}

/* A journal's legs on other deals, or on none. Hilbert Group's journal of
   22 January 2026 showed one credit to `Assets:Intercompany:HPI` on its row,
   because the debit to the fund's Mercury ••9567 is tagged to Functional
   Brands, and a lone leg reads as a defect. The collapsed row counts them; the
   open row lists them under this deal's own legs. */
export function otherLegsNote(txn) {
  const legs = (txn && txn.other_legs) || [];
  if (!legs.length) return "";
  const deals = [...new Set(legs.map((l) => l.deal || ""))];
  const n = `+${legs.length} leg${legs.length === 1 ? "" : "s"}`;
  if (deals.length > 1) return `${n} on other deals`;
  return deals[0] ? `${n} on ${deals[0]}` : `${n} tagged to no deal`;
}

const LEDGER_LABELS = {
  cash_out: "Cash out",
  cash_in: "Cash in",
  transfer: "Transfer between our own accounts",
  financing: "Financing flow",
  reversal: "Reversal",
  redate: "Correction moved into the month it belongs to",
  non_cash: "Journal, no cash",
};

const LOT_ACTIONS = {
  Buy: "Bought",
  Sell: "Sold",
  Convert: "Converted",
  Cover: "Covered",
  Paydown: "Paid down",
  ElocSettle: "Received on the equity line",
  PikCapitalize: "Interest capitalised",
  Exercise: "Exercised",
  ExchangeOut: "Exchanged in a merger",
  ExchangeIn: "Received in a merger",
};

const price = (value) => (toNumber(value) ? ` @ ${plainNumber(value, 4)}` : "");

/**
 * One line of English per event — what happened, said the way the business
 * says it — and, apart from it, the detail that opens with the row. `ctx`
 * carries the deal's name, so a description that closes on it does not repeat
 * the heading, and whether the deal's trades are in more than one security,
 * the only case where "Sold 355,579" needs the security's name to be read.
 */
export function describe(e, ctx = {}) {
  const clean = (text) => readerText(text, { dealName: ctx.dealName });
  switch (e.kind) {
    case "bank": {
      const dir = Number(e.amount) < 0 ? "Paid" : "Received from";
      const who = e.counterparty || "unnamed counterparty";
      return {
        what: `${dir} ${who}`,
        detail: [clean(e.memo), e.account && `via ${e.account}`, e.entity, !e.settled && `bank status: ${e.status || "not settled"} — no money moved`]
          .filter(Boolean)
          .join(" · "),
        cash: e.amount,
        currency: e.currency,
      };
    }
    case "trade": {
      const qty = Number(e.quantity);
      const verb =
        e.row_type === "TRADE"
          ? e.covers_short
            ? "Bought to cover"
            : qty < 0 || e.is_short
            ? (e.posted ? e.sold_short ?? e.is_short : e.is_short)
              ? "Short sold"
              : "Sold"
            : "Bought"
          : e.row_type === "DELIVERY"
          ? qty < 0
            ? "Delivered out"
            : "Received"
          : e.row_type === "CASH_WIRE"
          ? "Wire at broker"
          : e.row_type.charAt(0) + e.row_type.slice(1).toLowerCase();
      const name = e.security || e.symbol || e.cusip;
      return {
        what: `${verb} ${shares(qty)}${ctx.multiSecurity && name ? ` ${name}` : ""}${price(e.price)}`,
        detail: [
          !ctx.multiSecurity && name,
          `${e.broker} ${e.account}`,
          e.symbol && `symbol ${e.symbol}`,
          e.cusip && `CUSIP ${e.cusip}`,
          toNumber(e.fees) ? `fees ${plainNumber(e.fees)}` : null,
          e.settle_date && `settles ${day(e.settle_date)}`,
        ]
          .filter(Boolean)
          .join(" · "),
        cash: e.cash_delta ?? (qty < 0 ? e.net_amount : -Math.abs(Number(e.net_amount))),
        currency: e.currency,
      };
    }
    case "lot": {
      const buy = ["Buy", "Convert", "Cover", "ElocSettle", "Exercise", "ExchangeIn"].includes(e.action);
      const cost = Number(e.cost_basis);
      return {
        what: `${LOT_ACTIONS[e.action] || e.action} ${shares(e.quantity)} ${e.security}${price(e.unit_cost)}`,
        detail: [
          e.is_open ? "open" : `closed ${day(e.closed_date)}`,
          toNumber(e.realized_pnl) !== null ? `realised ${signedMoney(e.realized_pnl, e.currency)}` : null,
          clean(e.notes),
        ]
          .filter(Boolean)
          .join(" · "),
        cash: buy ? -cost : e.net_proceeds ?? null,
        currency: e.currency,
        cashLabel: buy ? "cost" : "proceeds",
      };
    }
    case "notice": {
      const parts = [];
      if (e.principal_converted) {
        parts.push(`${plainNumber(e.principal_converted)}${e.notice_type === "ELOC_ADVANCE" ? " advanced" : " face"}`);
      }
      if (e.interest_converted && Number(e.interest_converted)) parts.push(`${plainNumber(e.interest_converted)} interest`);
      const into = e.shares_issued ? `${shares(e.shares_issued)} shares` : "";
      const figures = [parts.join(" + "), into].filter(Boolean).join(" → ");
      return {
        what: `${e.notice_type_label}${e.sequence_number ? ` #${e.sequence_number}` : ""}${
          figures ? ` · ${figures}` : ""
        }${price(e.conversion_price)}`,
        detail: [e.issuer || e.security, e.title, (e.status || "").toLowerCase().replace("_", " ")]
          .filter(Boolean)
          .join(" · "),
        cash: null,
        currency: e.currency,
      };
    }
    default: {
      const label = e.ledger_kind === "intercompany" ? intercompanyLabel(e.intercompany) : LEDGER_LABELS[e.ledger_kind] || "Ledger";
      const sign = e.ledger_kind === "cash_out" ? -1 : e.ledger_kind === "cash_in" ? 1 : 0;
      const description = clean(e.description);
      return {
        what: `${label}${description ? `: ${description}` : ""}`,
        detail: [
          (e.legs || []).map((l) => `${sideLabel(l.side)} ${l.account}`).join(", "),
          otherLegsNote(e),
        ]
          .filter(Boolean)
          .join(" · "),
        cash: sign ? sign * Number(e.amount) : null,
        currency: e.currency,
      };
    }
  }
}

/* What is hidden until asked for. A superseded row describes no event at
   all — a reversed transaction, the reversal that undid it, a re-dating, a lot
   the unposter zeroed or retired and kept for the record (0.23). A zero-value
   row moved nothing. A notice moves no cash on its face and is never zero. */
export function isSuperseded(e) {
  if (e.kind === "ledger") return Boolean(e.superseded);
  if (e.kind === "lot") return isRetiredNote(e.notes);
  return false;
}

const nil = (value) => Math.abs(toNumber(value) ?? 0) < 0.005;

export function isZeroValue(e) {
  switch (e.kind) {
    case "bank":
      return nil(e.amount);
    case "trade":
      return nil(e.quantity) && nil(e.cash_delta ?? e.net_amount);
    case "lot":
      return nil(e.quantity) && nil(e.cost_basis) && nil(e.net_proceeds);
    case "ledger":
      return nil(e.amount);
    default:
      return false;
  }
}

/** The lots a trade or a notice made or drew on, which open under that row. */
export function ownedLotIds(events) {
  const lots = new Set((events || []).filter((e) => e.kind === "lot").map((e) => e.id));
  const owned = new Set();
  (events || []).forEach((e) => {
    if ((e.kind === "trade" || e.kind === "notice") && lots.has(e.tax_lot_id)) owned.add(e.tax_lot_id);
  });
  return owned;
}

export const ACTIVITY_TYPES = [
  { key: "all", label: "All activity" },
  { key: "bank", label: "Bank" },
  { key: "trade", label: "Trades" },
  { key: "lot", label: "Lots" },
  { key: "notice", label: "Notices" },
  { key: "ledger", label: "Ledger" },
];

/**
 * The rows the list shows, and how many it is holding back. "All activity" is
 * one row per thing that happened: a ledger transaction or lot that another
 * row wrote is under that row; a type picks one register whole.
 */
export function activityRows(events, { type = "all", from = "", to = "", showHidden = false } = {}) {
  const owned = ownedLotIds(events);
  const inType = (events || []).filter((e) => {
    if (type !== "all") return e.kind === type;
    if (e.kind === "lot") return !owned.has(e.id);
    if (e.kind === "ledger") return e.unlinked || (e.superseded && !(e.linked_from || []).length);
    return true;
  });
  const inDates = inType.filter((e) => {
    if (!from && !to) return true;
    if (!e.date) return false;
    return (!from || e.date >= from) && (!to || e.date <= to);
  });
  const hidden = inDates.filter((e) => isSuperseded(e) || isZeroValue(e));
  return {
    rows: showHidden ? inDates : inDates.filter((e) => !hidden.includes(e)),
    hiddenCount: hidden.length,
  };
}

/**
 * The month groups and each month's total of the cash that moved, by
 * currency — krona and dollars are listed side by side and never added. A
 * lot's figure is a cost or proceeds on the book, not money moving, so it is
 * in the column and not in the total. And the total is of the rows shown,
 * which is what it says it is: rows from two registers can describe the same
 * money (a sale at the broker and the wire that later brought its proceeds to
 * the bank), so it is not the deal's cash for the month. That is on the Cash
 * curve tab.
 */
export function monthGroups(rows, ctx = {}) {
  const groups = [];
  let current = null;
  rows.forEach((e) => {
    const month = e.date ? e.date.slice(0, 7) : "undated";
    if (!current || current.month !== month) {
      current = { month, rows: [], totals: {} };
      groups.push(current);
    }
    current.rows.push(e);
    const d = describe(e, ctx);
    const cash = toNumber(d.cash);
    if (cash !== null && !d.cashLabel) {
      const c = d.currency || "USD";
      current.totals[c] = (current.totals[c] || 0) + cash;
    }
  });
  return groups;
}

export const monthLabel = (month) =>
  month === "undated"
    ? "Undated"
    : new Date(`${month}-01T12:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });

export const totalsParts = (totals) =>
  Object.entries(totals || {})
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .map(([c, v]) => signedMoney(v, c));

export const totalsText = (totals) => totalsParts(totals).join(" · ");

export function statusText(e) {
  if (e.kind === "bank") {
    if (!e.settled) return e.status || "not settled";
    return e.posted ? "posted" : "not posted";
  }
  if (e.kind === "trade" || e.kind === "notice") return e.posted ? "posted" : "not posted";
  if (e.kind === "lot") return isRetiredNote(e.notes) ? "retired" : e.is_open ? "open" : "closed";
  if (e.superseded) return "superseded";
  if (e.unlinked) return "no source row";
  return "";
}

function StatusBadge({ e }) {
  const text = statusText(e);
  if (!text) return null;
  const variant =
    e.kind === "bank" && !e.settled
      ? "red"
      : text === "posted"
      ? "green"
      : text === "not posted" || text === "no source row"
      ? "amber"
      : text === "open"
      ? "blue"
      : "gray";
  return <Badge text={text} variant={variant} />;
}

function LegRow({ l, note }) {
  return (
    <tr>
      <td style={{ padding: "2px 10px 2px 0", color: theme.textMuted }}>{sideLabel(l.side)}</td>
      <td style={{ padding: "2px 16px 2px 0" }}>{l.account}</td>
      {/* A figure never wraps (0.37). */}
      <td style={{ padding: "2px 16px 2px 0", textAlign: "right", whiteSpace: "nowrap" }}>
        {money(l.amount, { currency: l.currency || "USD" })}
      </td>
      <td style={{ padding: "2px 16px 2px 0", color: theme.textMuted }}>{l.entity || "no entity"}</td>
      <td style={{ padding: "2px 0", color: theme.textMuted }}>{note}</td>
    </tr>
  );
}

/** A ledger transaction's legs: this deal's own, then the same journal's on other deals. */
export function Legs({ txn }) {
  const others = txn.other_legs || [];
  return (
    <table className="dr-legs">
      <tbody>
        {txn.legs.map((l, i) => (
          <LegRow key={i} l={l} note={readerText(l.description)} />
        ))}
        {others.length > 0 && (
          <tr>
            <td colSpan={5} style={{ paddingTop: 6, color: theme.textMuted }}>
              The same journal on other deals — not counted on this deal:
            </td>
          </tr>
        )}
        {others.map((l, i) => (
          <LegRow key={`o${i}`} l={l} note={l.deal ? `on ${l.deal}` : "tagged to no deal"} />
        ))}
        <tr>
          <td colSpan={5} style={{ paddingTop: 4 }}>
            {/* The id travels in the link, not on the screen (0.2): it is
                what somebody needs to find this exact row, and nothing else. */}
            <Link to={ledgerPath(txn.id)} className="dr-link" style={{ fontSize: 11 }}>
              Find this transaction in the Ledger
            </Link>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function LotLine({ lot, ctx }) {
  const d = describe(lot, ctx);
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 12, color: theme.text }}>
        <Badge text="Lot" variant="gray" style={{ marginRight: 6 }} />
        {d.what}
        {toNumber(d.cash) !== null && (
          <span style={{ color: theme.textMuted }}>
            {" "}· {d.cashLabel} {signedMoney(d.cash, d.currency)}
          </span>
        )}
      </div>
      {d.detail && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{d.detail}</div>}
    </div>
  );
}

function ActivityRow({ e, ctx, ledgerById, lotsById, open, onToggle }) {
  const d = describe(e, ctx);
  // Every posting the row wrote, not only the first: a row posted, unposted
  // and posted again has two, and showing one of them is how the other came
  // to read as a hand journal nobody wrote.
  const linkedIds =
    e.kind === "ledger"
      ? []
      : e.ledger_transaction_ids && e.ledger_transaction_ids.length
      ? e.ledger_transaction_ids
      : [e.ledger_transaction_id];
  const linked = linkedIds.map((id) => id && ledgerById[id]).filter(Boolean);
  const lot = (e.kind === "trade" || e.kind === "notice") && lotsById[e.tax_lot_id];
  const cash = toNumber(d.cash);
  const color = cash === null ? theme.text : cash < 0 ? theme.danger : cash > 0 ? theme.success : theme.text;
  const muted = isSuperseded(e) || isZeroValue(e);
  return (
    <>
      <tr className={`dr-act-row${open ? " is-open" : ""}${muted ? " is-muted" : ""}`} onClick={onToggle}>
        <td className="dr-act-date">{day(e.date)}</td>
        <td>
          <Badge text={KIND[e.kind].label} variant={KIND[e.kind].variant} />
        </td>
        <td className="dr-act-what" title={d.what}>
          {d.what}
        </td>
        <td className="dr-act-cash" style={{ color }}>
          {cash !== null ? signedMoney(cash, d.currency) : ""}
          {d.cashLabel && cash !== null && <span className="dr-act-cashlabel"> {d.cashLabel}</span>}
        </td>
        <td style={{ textAlign: "right" }}>
          <StatusBadge e={e} />
        </td>
        <td style={{ textAlign: "right" }}>
          <button
            type="button"
            className="dr-act-toggle"
            aria-expanded={open}
            aria-label={open ? "Hide the related records" : "Show the related records"}
            onClick={(ev) => {
              ev.stopPropagation();
              onToggle();
            }}
          >
            {open ? "▾" : "▸"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="dr-act-detail">
          <td />
          <td colSpan={5}>
            {d.detail && <div style={{ fontSize: 12, color: theme.textSecondary }}>{d.detail}</div>}
            {e.deal_share !== null && e.deal_share !== undefined && (
              <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>
                {signedMoney(e.deal_share, d.currency)} of it is this deal's share.
              </div>
            )}
            {lot && <LotLine lot={lot} ctx={ctx} />}
            {e.kind === "ledger" ? <Legs txn={e} /> : linked.map((txn) => <Legs key={txn.id} txn={txn} />)}
            {e.kind !== "ledger" && e.kind !== "lot" && linked.length === 0 && (
              <div style={{ fontSize: 11, color: theme.textMuted, paddingTop: 6 }}>
                No ledger transaction is linked to this row.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Read once per deal per visit: the Activity tab can be left and come back to
// without asking the server again. The Refresh on the page header reloads it.
const activityCache = new Map();

export function clearActivityCache() {
  activityCache.clear();
}

function useActivity(dealId) {
  const [state, setState] = useState(() => ({ data: activityCache.get(dealId) || null, error: "" }));
  useEffect(() => {
    let live = true;
    const cached = activityCache.get(dealId);
    setState({ data: cached || null, error: "" });
    if (cached) return undefined;
    api
      .get("/reports/deal-activity/", { params: { deal: dealId } })
      .then((res) => {
        activityCache.set(dealId, res.data);
        if (live) setState({ data: res.data, error: "" });
      })
      .catch((err) => live && setState({ data: null, error: err.response?.data?.error || err.message || "Failed to load" }));
    return () => {
      live = false;
    };
  }, [dealId]);
  return state;
}

const control = {
  padding: "5px 8px",
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radius,
  background: theme.bgInput,
  color: theme.text,
  fontSize: 12,
  fontFamily: theme.fontBody,
};

export function DealActivity({ deal }) {
  const dealId = deal.deal.id;
  const { data, error } = useActivity(dealId);
  const [type, setType] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    setOpen({});
  }, [dealId]);

  const events = useMemo(() => data?.events || [], [data]);
  const ledgerById = useMemo(() => Object.fromEntries(events.filter((e) => e.kind === "ledger").map((e) => [e.id, e])), [events]);
  const lotsById = useMemo(() => Object.fromEntries(events.filter((e) => e.kind === "lot").map((e) => [e.id, e])), [events]);
  const ctx = useMemo(() => {
    const securities = new Set(events.filter((e) => e.kind === "trade").map((e) => e.security || e.symbol));
    return { dealName: deal.deal.name, multiSecurity: securities.size > 1 };
  }, [events, deal.deal.name]);
  const { rows, hiddenCount } = useMemo(
    () => activityRows(events, { type, from, to, showHidden }),
    [events, type, from, to, showHidden]
  );
  const groups = useMemo(() => monthGroups(rows, ctx), [rows, ctx]);
  const dates = useMemo(() => events.map((e) => e.date).filter(Boolean).sort(), [events]);

  if (error) return <Card title="Activity"><EmptyState message={error} icon="⚠" /></Card>;
  if (!data) return <Card title="Activity"><LoadingSpinner message="Reading the registers…" /></Card>;

  const c = data.counts || {};
  const typeCount = {
    all: activityRows(events, { type: "all", showHidden: true }).rows.length,
    bank: c.bank,
    trade: c.trade,
    lot: c.lot,
    notice: c.notice,
    ledger: c.ledger,
  };
  const warnings = [
    c.bank_unposted && `${c.bank_unposted} settled bank ${c.bank_unposted === 1 ? "row is" : "rows are"} not posted to the ledger`,
    c.trade_unposted && `${c.trade_unposted} broker ${c.trade_unposted === 1 ? "trade is" : "trades are"} not posted`,
    c.bank_failed && `${c.bank_failed} bank ${c.bank_failed === 1 ? "row" : "rows"} failed or pending — shown, but no money moved`,
    c.ledger_unlinked && `${c.ledger_unlinked} ledger ${c.ledger_unlinked === 1 ? "transaction has" : "transactions have"} no bank row, trade or notice behind ${c.ledger_unlinked === 1 ? "it" : "them"}`,
  ].filter(Boolean);

  const csvColumns = [
    { label: "Date", value: (e) => e.date || "" },
    { label: "Source", value: (e) => KIND[e.kind].label },
    { label: "What happened", value: (e) => describe(e, ctx).what },
    { label: "Detail", value: (e) => describe(e, ctx).detail },
    { label: "Cash", value: (e) => toNumber(describe(e, ctx).cash) },
    { label: "Currency", value: (e) => describe(e, ctx).currency || "" },
    { label: "Status", value: (e) => statusText(e) },
  ];
  const filtered = type !== "all" || from || to;

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Activity
          <InfoTip wide>
            <p>
              One row per thing that happened, in date order. Open a row for the records behind it:
              the ledger transactions it wrote and the lot it made or drew on.
            </p>
            <p>
              Cash is from the fund's side — money out in brackets, money back with a plus — in the
              currency the source recorded. A bank row is the bank's statement, a trade is the
              broker's, a lot is the book's position, a notice is the counterparty's paper, and the
              ledger is the auditable record; where they disagree, that is the thing to look at.
            </p>
            <p>
              {c.ledger || 0} ledger transactions are on this deal: {c.ledger_sourced || 0} sit under
              the row that wrote them, {c.ledger_superseded || 0} were reversed or re-dated, and{" "}
              {c.ledger_unlinked || 0} have a row of their own.
            </p>
          </InfoTip>
        </span>
      }
      subtitle={`${rows.length} ${rows.length === 1 ? "row" : "rows"}${filtered ? " in this filter" : ""}, newest month last`}
      action={<ExportCsvButton filename={`deal-activity-${deal.deal.name}`} columns={csvColumns} rows={rows} />}
    >
      <div className="dr-act-controls no-print">
        <label className="dr-field">
          <span>Type</span>
          <select value={type} onChange={(ev) => setType(ev.target.value)} style={control} aria-label="Type">
            {ACTIVITY_TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label} ({typeCount[t.key] || 0})
              </option>
            ))}
          </select>
        </label>
        <label className="dr-field">
          <span>From</span>
          <input type="date" aria-label="From" value={from} min={dates[0]} max={dates[dates.length - 1]} onChange={(ev) => setFrom(ev.target.value)} style={control} />
        </label>
        <label className="dr-field">
          <span>To</span>
          <input type="date" aria-label="To" value={to} min={dates[0]} max={dates[dates.length - 1]} onChange={(ev) => setTo(ev.target.value)} style={control} />
        </label>
        {(from || to) && (
          <button type="button" className="pt-link" onClick={() => { setFrom(""); setTo(""); }}>
            Clear dates
          </button>
        )}
        <label className="dr-check">
          <input type="checkbox" checked={showHidden} onChange={(ev) => setShowHidden(ev.target.checked)} />
          Show superseded &amp; zero-value rows ({hiddenCount})
        </label>
      </div>

      {warnings.length > 0 && (
        <div className="dr-warning" style={{ fontSize: 12, marginBottom: 12 }}>
          {warnings.map((w) => (
            <div key={w}>• {w}</div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState
          message={
            hiddenCount && !showHidden
              ? `Nothing to show here except ${hiddenCount} superseded or zero-value ${hiddenCount === 1 ? "row" : "rows"}.`
              : "Nothing in this register for this deal in this range."
          }
          icon="◌"
        />
      ) : (
        <div className="dr-act-scroll">
          <table className="dr-act">
            <colgroup>
              <col style={{ width: 96 }} />
              <col style={{ width: 70 }} />
              <col />
              <col style={{ width: 168 }} />
              <col style={{ width: 104 }} />
              <col style={{ width: 28 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Source</th>
                <th>What happened</th>
                <th style={{ textAlign: "right" }}>Cash</th>
                <th style={{ textAlign: "right" }}>Status</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.month}>
                <tr className="dr-act-month">
                  <td colSpan={3}>
                    {monthLabel(g.month)}
                    <span className="dr-act-monthcount"> · {g.rows.length} {g.rows.length === 1 ? "row" : "rows"}</span>
                  </td>
                  {/* Across the cash and status columns, so a month with
                      krona and dollars in it has room for both on one line;
                      each figure stays whole if it has to wrap (0.37). */}
                  <td
                    colSpan={3}
                    className="dr-act-cash dr-act-monthtotal"
                    title="The cash that moved in the rows shown for this month, by currency; a lot's cost or proceeds is not counted. Rows from two registers can describe the same money, so this is not the deal's cash for the month — that is on the Cash curve tab."
                  >
                    {Object.keys(g.totals).length > 0 && (
                      <>
                        <span className="dr-act-monthnote">cash in the month </span>
                        {totalsParts(g.totals).map((part, i) => (
                          <span key={part} className="dr-nowrap">
                            {i ? " · " : ""}
                            {part}
                          </span>
                        ))}
                      </>
                    )}
                  </td>
                </tr>
                {g.rows.map((e) => {
                  const key = `${e.kind}-${e.id}`;
                  return (
                    <ActivityRow
                      key={key}
                      e={e}
                      ctx={ctx}
                      ledgerById={ledgerById}
                      lotsById={lotsById}
                      open={!!open[key]}
                      onToggle={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                    />
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </Card>
  );
}
